/**
 * @file environment.ts
 * @description Runtime environment resolver.
 *
 * Detects `window.location.hostname` at app boot and exports the matching
 * config from one of three peer files:
 *   - `environment.development.ts` — dev backends (api-dev, auth-dev)
 *   - `environment.staging.ts`     — dev backends, prod-optimized build
 *   - `environment.production.ts`  — live backends (api-prod, auth-prod)
 *
 * Matching rules:
 *   - `localhost` / `127.0.0.1` / `*.local` / private LAN   → **development**
 *   - `dev.kirigami.ai` / `*.dev.kirigami.ai`               → **staging**
 *   - S3 static-website hosts (`*.s3-website-*.amazonaws.com`
 *     and `*.s3-website.*.amazonaws.com`) used for POC/preview
 *     deploys                                               → **staging**
 *   - everything else                                       → **production**
 *
 * One bundle ships to all environments; the right config activates at boot.
 * File-replacement build-time swapping has been removed in favour of this
 * runtime resolver — all three configs are imported statically and the
 * resolver picks one.
 *
 * Consumers continue to import from `../../environments/environment` — the
 * exported `environment` symbol is unchanged in shape (still `Environment`).
 */
import type { Environment } from './environment.model';
import { environment as developmentEnvironment } from './environment.development';
import { environment as stagingEnvironment } from './environment.staging';
import { environment as productionEnvironment } from './environment.production';

export type { Environment } from './environment.model';

/** Hostnames that resolve to the `development` config. */
const DEV_HOSTNAMES = new Set(['localhost', '127.0.0.1', '0.0.0.0']);

/** Hostname suffixes that resolve to the `staging` config. */
const STAGING_HOSTNAME_SUFFIXES = ['dev.kirigami.ai'];

/**
 * Detects AWS S3 static-website endpoints (both legacy and modern formats):
 *   - `<bucket>.s3-website-<region>.amazonaws.com`  (legacy, hyphen)
 *   - `<bucket>.s3-website.<region>.amazonaws.com`  (newer, dot)
 *
 * Used for throw-away POC / preview deploys that should always hit dev
 * backends, never prod. If a bucket is ever repurposed for real production
 * traffic, map it to a proper domain name instead of relying on this pattern.
 */
function isS3WebsiteHost(hostname: string): boolean {
  if (!hostname.endsWith('.amazonaws.com')) return false;
  return hostname.includes('.s3-website-') || hostname.includes('.s3-website.');
}

/**
 * Picks the environment config based on the current browser hostname.
 * Falls back to production when `window` is unavailable (e.g. SSR) or
 * when the hostname doesn't match any known pattern.
 */
function resolveEnvironment(): Environment {
  if (typeof window === 'undefined' || !window.location) {
    return productionEnvironment;
  }

  const hostname = window.location.hostname.toLowerCase();

  // Private / local dev hosts → development
  if (
    DEV_HOSTNAMES.has(hostname) ||
    hostname.endsWith('.local') ||
    hostname.startsWith('192.168.') ||
    hostname.startsWith('10.')
  ) {
    return developmentEnvironment;
  }

  // Staging hosts (canonical dev subdomain) → staging
  for (const suffix of STAGING_HOSTNAME_SUFFIXES) {
    if (hostname === suffix || hostname.endsWith(`.${suffix}`)) {
      return stagingEnvironment;
    }
  }

  // S3 static-website hosts (POC / preview deploys) → staging
  if (isS3WebsiteHost(hostname)) {
    return stagingEnvironment;
  }

  // Everything else → production
  return productionEnvironment;
}

export const environment: Environment = resolveEnvironment();
